import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { withFormik } from "formik";
import * as Yup from "yup";
import InputWithBottomBorder from "shared/components/InputWithBottomBorder";
import {
	formatExpiration,
	formatCardNumber,
	formatCvv,
	formatZipCode,
} from "shared/payment/helpers/format-value";
import ProductInfo from "apps/payment/components/ProductInfo";
import PaymentFormSubmitButton from "apps/payment/components/PaymentFormSubmitButton";
import { PRODUCT_SUBTYPES } from "shared/payment/constants";
import CardIcon from "./CardIcon";
import { getCreditCardTypeFromNumber } from "shared/payment/helpers/credit-card";
import _str from "shared/util/_str";
import {
	selectedPaymentTypeOnCheckoutEvent,
} from "shared/payment/analytics";

import CVVIcon from "./cvv.svg";

import "./CreditCardForm.scss";

const CreditCardForm = ({
	values,
	touched,
	errors,
	handleChange,
	handleBlur,
	handleSubmit,
	shouldShowZipCode,
	setFieldValue,
	productName,
	taxAmount,
	formattedInitialPriceWithoutDiscount,
	productPrice,
	productSubtype,
	totalPrice,
	isInternational,
	hasVATIncluded,
	convertedProductPrice,
	rateCardName,
	productType,
	paymentOptions,
	currency,
	subscriptionSubPeriod,
	tokensCount,
	subPeriodType,
	isProcessing,
	hasSuccessfullyPurchased,
	isUserInWashingtonState,
	upgradePrice,
}) => {
	useEffect(() => {
		selectedPaymentTypeOnCheckoutEvent({
			upgradePrice,
			rateCardName,
			isDefaultPaymentType: true,
			isSavedCreditCard: false,
			paymentType: "cc",
			productSubtype,
			productType,
			paymentOptions,
			currency,
			subscriptionSubPeriod,
			tokensCount,
			unitPrice: productPrice,
			subPeriodType,
		});

	// eslint-disable-next-line
	}, []);

	const cardType = getCreditCardTypeFromNumber(values.cardNumber);

	return (
		<form
			className="credit-card-form"
			onSubmit={handleSubmit}
			autoComplete="on"
		>
			<div className="credit-card-form-inputs-container">
				<div className="credit-card-form-cc-number">
					<InputWithBottomBorder
						name="cardNumber"
						placeholder={_str("Card number")}
						value={values.cardNumber}
						onChange={(evt) => {
							const formattedCardNumber = formatCardNumber({
								newValue: evt.target.value,
								oldValue: values.cardNumber,
							});
							setFieldValue("cardNumber", formattedCardNumber);
						}}
						error={touched.cardNumber && errors.cardNumber}
						onBlur={handleBlur}
						autoComplete="cc-number"
					/>
					{cardType &&
						<CardIcon
							className="credit-card-form-card-icon"
							cardType={cardType}
						/>
					}
				</div>
				<InputWithBottomBorder
					name="fullName"
					placeholder={_str("Name on card")}
					value={values.fullName}
					onChange={handleChange}
					error={touched.fullName && errors.fullName}
					onBlur={handleBlur}
					autoComplete="cc-name"
				/>
				<div
					className="credit-card-form-exp-cvv-container"
				>
					<InputWithBottomBorder
						name="expirationDate"
						placeholder={_str("MM/YY")}
						value={values.expirationDate}
						onChange={(evt) => {
							const formattedExpiration = formatExpiration({
								newValue: evt.target.value,
								oldValue: values.expirationDate,
							});
							setFieldValue("expirationDate", formattedExpiration);
						}}
						error={touched.expirationDate && errors.expirationDate}
						onBlur={handleBlur}
						maxLength={7}
						autoComplete="cc-exp"
					/>
					<div className="credit-card-form-cvv-container">
						<InputWithBottomBorder
							name="cvv"
							placeholder={_str("CVV")}
							value={values.cvv}
							onChange={(evt) => {
								const formattedCVV = formatCvv({
									newValue: evt.target.value,
									oldValue: values.cvv,
								});
								setFieldValue("cvv", formattedCVV);
							}}
							error={touched.cvv && errors.cvv}
							onBlur={handleBlur}
							maxLength={4}
							autoComplete="cc-cvv"
							pattern="\d*"
							type="number"
						/>
						<CVVIcon
							className="credit-card-form-cvv-icon"
						/>
					</div>
				</div>
				{shouldShowZipCode
							&& (
								<div className="credit-card-form-zip-code">
									<InputWithBottomBorder
										name="zipCode"
										placeholder={_str("Zip code")}
										value={values.zipCode}
										onChange={(evt) => {
											const formattedZipCode = formatZipCode({
												newValue: evt.target.value,
												oldValue: values.zipCode,
											});
											setFieldValue("zipCode", formattedZipCode);
										}}
										error={touched.zipCode && errors.zipCode}
										onBlur={handleBlur}
										autoComplete="postal-code"
									/>
								</div>
							)
				}
				<div className="credit-card-form-product-info-container">
					<ProductInfo
						productName={productName}
						taxAmount={taxAmount}
						productPrice={productPrice}
						productSubtype={productSubtype}
						totalPrice={totalPrice}
						hasVATIncluded={hasVATIncluded}
						isInternational={isInternational}
						convertedProductPrice={convertedProductPrice}
						isUserInWashingtonState={isUserInWashingtonState}
						formattedInitialPriceWithoutDiscount={formattedInitialPriceWithoutDiscount}
					/>
				</div>
			</div>
			<PaymentFormSubmitButton
				productSubtype={productSubtype}
				isProcessing={isProcessing}
				hasSuccessfullyPurchased={hasSuccessfullyPurchased}
			/>
		</form>
	);
};



CreditCardForm.propTypes = {
	handleChange: PropTypes.func.isRequired,
	values: PropTypes.shape({
		cardNumber: PropTypes.string,
		fullName: PropTypes.string,
		expirationDate: PropTypes.string,
		cvv: PropTypes.string,
		zipCode: PropTypes.string,
	}).isRequired,
	errors: PropTypes.shape({
		cardNumber: PropTypes.string,
		fullName: PropTypes.string,
		expirationDate: PropTypes.string,
		cvv: PropTypes.string,
		zipCode: PropTypes.string,
	}).isRequired,
	handleBlur: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	touched: PropTypes.shape({
		cardNumber: PropTypes.bool,
		fullName: PropTypes.bool,
		expirationDate: PropTypes.bool,
		cvv: PropTypes.bool,
		zipCode: PropTypes.bool,
	}).isRequired,
	shouldShowZipCode: PropTypes.bool,
	setFieldValue: PropTypes.func.isRequired,
	productName: PropTypes.string.isRequired,
	taxAmount: PropTypes.string,
	formattedInitialPriceWithoutDiscount: PropTypes.string,
	productPrice: PropTypes.string.isRequired,
	productSubtype: PropTypes.oneOf(Object.values(PRODUCT_SUBTYPES)).isRequired,
	totalPrice: PropTypes.string.isRequired,
	hasVATIncluded: PropTypes.bool,
	isInternational: PropTypes.bool,
	convertedProductPrice: PropTypes.string,
	rateCardName: PropTypes.string,
	productType: PropTypes.string.isRequired,
	paymentOptions: PropTypes.array.isRequired,
	currency: PropTypes.string.isRequired,
	subscriptionSubPeriod: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	tokensCount: PropTypes.number,
	subPeriodType: PropTypes.string,
	isProcessing: PropTypes.bool,
	hasSuccessfullyPurchased: PropTypes.bool,
	isUserInWashingtonState: PropTypes.bool,
	upgradePrice: PropTypes.number,
};

CreditCardForm.defaultProps = {
	rateCardName: null,
	shouldShowZipCode: false,
	taxAmount: null,
	hasVATIncluded: false,
	isInternational: false,
	convertedProductPrice: null,
	subscriptionSubPeriod: null,
	tokensCount: null,
	subPeriodType: null,
	isProcessing: false,
	hasSuccessfullyPurchased: false,
	isUserInWashingtonState: false,
	upgradePrice: null,
	formattedInitialPriceWithoutDiscount: null,
};

const EnhancedCreditCardForm = withFormik({
	mapPropsToValues: (props) => ({
		cardNumber: props.cardNumber,
		fullName: props.fullName,
		expirationDate: props.expirationDate,
		cvv: props.cvv,
		zipCode: props.zipCode,
		shouldShowZipCode: props.shouldShowZipCode,
	}),
	handleSubmit: (values, { props }) => {
		props.onSubmit(values);
	},
	displayName: "CreditCardForm",
	validationSchema: Yup.object().shape({
		cardNumber: Yup.string()
			.min(12, "A card is 12 to 19 numbers")
			.max(19, "A card is 12 to 19 numbers")
			.required("Required"),
		fullName: Yup.string()
			.required("Required"),
		expirationDate: Yup.string()
			.min(7, "Dates must be MM/YY")
			.required("Required"),
		cvv: Yup.string()
			.min(3, "CVV’s are 3-4 numbers")
			.max(4, "CVV’s are 3-4 numbers")
			.required("Required"),
		shouldShowZipCode: Yup.boolean(),
		zipCode: Yup.string()
			.when("shouldShowZipCode", {
				is: true,
				then: Yup.string().required("Required"),
			}),
	}),
})(CreditCardForm);

export default EnhancedCreditCardForm;




// WEBPACK FOOTER //
// ./src/apps/payment/components/CreditCardForm/CreditCardForm.jsx